export const agentChatAPIs = [
  {
    title: 'Validate an API key\'s authenticity and scopes',
    method: 'POST',
    endpoint: '/api/key/validate',
    description: 'Check if an API key is valid and retrieve its details. This should be your first step to verify authentication.',
    body: {
      key: {
        type: 'string',
        required: true,
        description: 'The API key to validate',
        format: 'API key string (typically starts with prefix)',
        example: 'your-api-key-here'
      }
    },
    response: 'JSON response with key validation details'
  },
  {
    title: 'List all supported models',
    method: 'GET',
    endpoint: '/api/models',
    description: 'Retrieve the list of models available for use. Required before sending chat messages or generating responses.',
    headers: [
      { name: 'x-api-key', required: true, description: 'Your API key for authentication' }
    ],
    response: 'JSON response with available models array'
  },
  {
    title: 'Check health/status of each model',
    method: 'GET',
    endpoint: '/api/models/health',
    description: 'Get the operational health status of each model.',
    headers: [
      { name: 'x-api-key', required: true, description: 'Your API key for authentication' }
    ],
    response: 'JSON response with model health statuses'
  },
  {
    title: 'Create a new chat session',
    method: 'POST',
    endpoint: '/api/session',
    description: 'Start a new chat session with a title. You need a session before you can send chat messages.',
    headers: [
      { name: 'x-api-key', required: true, description: 'Your API key for authentication' }
    ],
    body: {
      title: {
        type: 'string',
        required: false,
        default: '"New Chat"',
        description: 'The session title',
        maxLength: 200,
        example: 'Customer Support Chat'
      }
    },
    response: 'JSON response with session metadata including session ID'
  },
  {
    title: 'List all sessions (with pagination)',
    method: 'GET',
    endpoint: '/api/session?page={page}&limit={limit}',
    description: 'Retrieve a paginated list of chat sessions.',
    headers: [
      { name: 'x-api-key', required: true, description: 'Your API key for authentication' }
    ],
    queryParams: {
      search: {
        type: 'string',
        required: false,
        description: 'Search query to filter sessions',
        example: 'customer support'
      },
      page: {
        type: 'number (integer)',
        required: false,
        default: 1,
        description: 'Page number for pagination',
        minimum: 1,
        example: 1
      },
      limit: {
        type: 'number (integer)',
        required: false,
        default: 10,
        description: 'Number of items per page',
        range: '1-100',
        example: 10
      }
    },
    response: 'JSON response with sessions list'
  },
  {
    title: 'Send a chat message in a session',
    method: 'POST',
    endpoint: '/api/chat',
    description: 'Send a chat message to a given session. Requires a session ID from creating a session.',
    usesFormData: true,
    headers: [
      { name: 'x-api-key', required: true, description: 'Your API key for authentication' }
    ],
    body: {
      message: {
        type: 'string',
        required: true,
        description: 'The chat message content',
        example: 'What is the weather today?'
      },
      session: {
        type: 'string (UUID)',
        required: true,
        description: 'The session ID from creating a session',
        example: 'dda2b18e-382c-4e1a-9469-4e397e79f89b',
        note: 'Obtain this from the Create a new chat session endpoint'
      },
      domain: {
        type: 'string',
        required: false,
        default: '"main"',
        description: 'The domain context for the conversation',
        options: ['main', 'custom-domain-name'],
        example: 'main'
      },
      model: {
        type: 'string',
        required: true,
        description: 'The model identifier to use',
        example: 'deepseek-ai/DeepSeek-R1-Distill-Qwen-7B',
        note: 'Use /api/models endpoint to get available models'
      },
      skip_rag: {
        type: 'boolean',
        required: false,
        default: 'false',
        description: 'Whether to skip RAG retrieval',
        options: [true, false],
        example: false
      },
      search: {
        type: 'boolean',
        required: false,
        default: 'true',
        description: 'Whether to perform web search',
        options: [true, false],
        example: true
      },
      audioFile: {
        type: 'file',
        required: false,
        description: 'Audio file for voice input',
        acceptedFormats: ['audio/wav', 'audio/mp3', 'audio/mpeg', 'audio/webm'],
        example: 'voice_input.wav'
      }
    },
    response: 'Server-Sent Events stream with message data',
    note: 'This endpoint uses FormData for file uploads and returns Server-Sent Events for streaming responses'
  },
  {
    title: 'Retrieve all messages in a specific session',
    method: 'GET',
    endpoint: '/api/session/{sessionId}',
    description: 'Get message history for one session ID. Requires an existing session.',
    headers: [
      { name: 'x-api-key', required: true, description: 'Your API key for authentication' }
    ],
    response: 'JSON response with session messages'
  },
  {
    title: 'Update or rate an existing assistant message',
    method: 'PATCH',
    endpoint: '/api/message',
    description: 'Patch an existing assistant message to mark it as good or bad. Requires a message ID from a chat response.',
    headers: [
      { name: 'x-api-key', required: true, description: 'Your API key for authentication' }
    ],
    body: {
      message_id: {
        type: 'string (UUID)',
        required: true,
        description: 'The ID of the message to rate (obtained from chat response)',
        example: 'f5584e7c-220f-4e81-b5e8-c0168153249f',
        note: 'Get this from the message response when sending a chat message'
      },
      is_good: {
        type: 'boolean',
        required: true,
        description: 'Feedback rating for the message',
        options: [true, false],
        examples: {
          positive: true,
          negative: false
        }
      }
    },
    response: 'JSON response with status and message'
  },
  {
    title: 'Upload and ingest a document into the knowledge base',
    method: 'POST',
    endpoint: '/api/file/ingest',
    description: 'Ingest a single document file and add it to the knowledge base. Required for RAG-powered responses.',
    usesFormData: true,
    headers: [
      { name: 'x-api-key', required: true, description: 'Your API key for authentication' }
    ],
    body: {
      files: {
        type: 'file',
        required: true,
        description: 'Document file to upload and process',
        acceptedFormats: ['application/pdf', 'text/plain', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/markdown', 'text/html'],
        maxSize: '50MB',
        example: 'document.pdf'
      },
      domain: {
        type: 'string',
        required: false,
        default: '"main"',
        description: 'The domain context',
        options: ['main', 'custom-domain-name'],
        example: 'main'
      }
    },
    response: 'JSON response with upload confirmation',
    note: 'This endpoint uses FormData for file upload'
  },
  {
    title: 'Fetch metadata for all uploaded files',
    method: 'GET',
    endpoint: '/api/files',
    description: 'List metadata for every document file in the system.',
    headers: [
      { name: 'x-api-key', required: true, description: 'Your API key for authentication' }
    ],
    response: 'JSON response with list of file metadata'
  },
  {
    title: 'Fetch metadata for a single file by its ID',
    method: 'GET',
    endpoint: '/api/file/{fileId}',
    description: 'Retrieve file metadata using a specific file ID. Use file ID from upload or list files endpoints.',
    headers: [
      { name: 'x-api-key', required: true, description: 'Your API key for authentication' }
    ],
    response: 'JSON response with single file metadata'
  },
  {
    title: 'Check the status of a bulk upload',
    method: 'GET',
    endpoint: '/api/file/upload/{uploadId}',
    description: 'Check progress and status for a batch file upload. Use upload ID from upload response.',
    headers: [
      { name: 'x-api-key', required: true, description: 'Your API key for authentication' }
    ],
    response: 'JSON response with upload status'
  },
  {
    title: 'Start a web crawl (URL ingestion)',
    method: 'POST',
    endpoint: '/api/file/crawl',
    description: 'Initiate a web crawl from a URL to ingest pages into the knowledge base for RAG.',
    headers: [
      { name: 'x-api-key', required: true, description: 'Your API key for authentication' }
    ],
    body: {
      url: {
        type: 'string (URL)',
        required: true,
        description: 'The URL to crawl',
        format: 'Must be a valid HTTP/HTTPS URL',
        example: 'https://example.com'
      },
      max_page: {
        type: 'number (integer)',
        required: false,
        default: 100,
        description: 'Maximum number of pages to crawl',
        range: '1-1000',
        example: 100
      },
      max_depth: {
        type: 'number (integer)',
        required: false,
        default: 3,
        description: 'Maximum crawl depth from the starting URL',
        range: '1-10',
        example: 3
      },
      crawl_sub_domain: {
        type: 'boolean',
        required: false,
        default: 'true',
        description: 'Whether to crawl subdomains',
        options: [true, false],
        example: true
      },
      fetch_files: {
        type: 'boolean',
        required: false,
        default: 'false',
        description: 'Whether to download PDF and other files found on pages',
        options: [true, false],
        example: false
      }
    },
    response: 'JSON response with crawl initiation metadata'
  },
  {
    title: 'Generate a response (RAG-powered)',
    method: 'POST',
    endpoint: '/api/generate',
    description: 'Generate an answer to a query using the specified RAG model. Uses knowledge base from uploaded files or crawled URLs.',
    headers: [
      { name: 'x-api-key', required: true, description: 'Your API key for authentication' }
    ],
    body: {
      query: {
        type: 'string',
        required: true,
        description: 'The question or prompt to generate a response for',
        example: 'What is artificial intelligence?'
      },
      model: {
        type: 'string',
        required: true,
        description: 'The model identifier to use',
        example: 'deepseek-ai/DeepSeek-R1-Distill-Qwen-7B',
        note: 'Use /api/models endpoint to get available models'
      },
      domain: {
        type: 'string',
        required: false,
        default: '"main"',
        description: 'The domain context',
        options: ['main', 'custom-domain-name'],
        example: 'main'
      },
      stream: {
        type: 'boolean',
        required: false,
        default: 'true',
        description: 'Whether to stream the response',
        options: [true, false],
        examples: {
          streaming: true,
          nonStreaming: false
        }
      }
    },
    response: 'Streamed token data or JSON response'
  },
  {
    title: 'Update the domain whitelist for an API key',
    method: 'PUT',
    endpoint: '/api/keys/{keyId}/domains',
    description: 'Modify the allowed domains for a given API key.',
    headers: [
      { name: 'x-api-key', required: true, description: 'Your API key for authentication' }
    ],
    body: {
      whitelisted_domains: {
        type: 'array of strings',
        required: true,
        description: 'Array of allowed domain URLs for CORS',
        format: 'Array of URL strings',
        example: ['http://piovation.de/', 'https://example.com'],
        minItems: 1
      }
    },
    response: 'JSON response with update confirmation'
  }
];

export const openaiSDKCompatibility = {
  title: 'OpenAI SDK Compatibility',
  description: 'PioSphere endpoints are compatible with the OpenAI SDK, allowing you to use the official OpenAI client libraries with minimal configuration changes. This enables easy migration from OpenAI and integration with existing codebases.',
  baseUrl: 'https://YOUR_AGENT_ENDPOINT',
  sections: [
    {
      title: 'Installation',
      content: 'Install the OpenAI SDK for your preferred language:',
      codeExamples: {
        javascript: 'npm install openai',
        python: 'pip install openai',
        curl: 'Not applicable - use direct API calls'
      }
    },
    {
      title: 'Configuration',
      content: 'Configure the OpenAI client to point to your PioSphere endpoint:',
      codeExamples: {
        javascript: `import OpenAI from 'openai';\n\nconst openai = new OpenAI({\n  apiKey: 'YOUR_API_KEY',\n  baseURL: 'https://YOUR_AGENT_ENDPOINT/v1'\n});`,
        python: `from openai import OpenAI\n\nclient = OpenAI(\n    api_key='YOUR_API_KEY',\n    base_url='https://YOUR_AGENT_ENDPOINT/v1'\n)`,
        curl: 'Use x-api-key header instead of Authorization: Bearer'
      }
    },
    {
      title: 'Chat Completions',
      content: 'Use the chat completions endpoint with OpenAI SDK syntax:',
      codeExamples: {
        javascript: `const completion = await openai.chat.completions.create({\n  model: 'deepseek-ai/DeepSeek-R1-Distill-Qwen-7B',\n  messages: [\n    { role: 'system', content: 'You are a helpful assistant.' },\n    { role: 'user', content: 'Hello!' }\n  ],\n  stream: true\n});\n\nfor await (const chunk of completion) {\n  process.stdout.write(chunk.choices[0]?.delta?.content || '');\n}`,
        python: `stream = client.chat.completions.create(\n    model='deepseek-ai/DeepSeek-R1-Distill-Qwen-7B',\n    messages=[\n        {'role': 'system', 'content': 'You are a helpful assistant.'},\n        {'role': 'user', 'content': 'Hello!'}\n    ],\n    stream=True\n)\n\nfor chunk in stream:\n    if chunk.choices[0].delta.content is not None:\n        print(chunk.choices[0].delta.content, end='')`,
        curl: `curl https://YOUR_AGENT_ENDPOINT/v1/chat/completions \\\n  -H "Content-Type: application/json" \\\n  -H "x-api-key: YOUR_API_KEY" \\\n  -d '{\n    "model": "deepseek-ai/DeepSeek-R1-Distill-Qwen-7B",\n    "messages": [\n      {"role": "user", "content": "Hello!"}\n    ],\n    "stream": true\n  }'`
      }
    },
    {
      title: 'List Models',
      content: 'Retrieve available models using the OpenAI SDK:',
      codeExamples: {
        javascript: `const models = await openai.models.list();\nconsole.log(models.data);`,
        python: `models = client.models.list()\nfor model in models.data:\n    print(model.id)`,
        curl: `curl https://YOUR_AGENT_ENDPOINT/v1/models \\\n  -H "x-api-key: YOUR_API_KEY"`
      }
    },
    {
      title: 'Tool Use (Function Calling)',
      content: 'Use tools (function calling) to enable the model to call your functions. Define your tools and pass them in the tools parameter:',
      codeExamples: {
        javascript: `const tools = [\n  {\n    type: 'function',\n    function: {\n      name: 'get_weather',\n      description: 'Get the current weather in a given location',\n      parameters: {\n        type: 'object',\n        properties: {\n          location: {\n            type: 'string',\n            description: 'The city and state, e.g. San Francisco, CA'\n          },\n          unit: {\n            type: 'string',\n            enum: ['celsius', 'fahrenheit']\n          }\n        },\n        required: ['location']\n      }\n    }\n  }\n];\n\nconst completion = await openai.chat.completions.create({\n  model: 'deepseek-ai/DeepSeek-R1-Distill-Qwen-7B',\n  messages: [\n    { role: 'user', content: 'What is the weather in San Francisco?' }\n  ],\n  tools: tools,\n  tool_choice: 'auto'\n});\n\nconst message = completion.choices[0].message;\nif (message.tool_calls) {\n  for (const toolCall of message.tool_calls) {\n    if (toolCall.function.name === 'get_weather') {\n      const args = JSON.parse(toolCall.function.arguments);\n      const weatherResult = await getWeather(args.location, args.unit);\n      \n      const followUp = await openai.chat.completions.create({\n        model: 'deepseek-ai/DeepSeek-R1-Distill-Qwen-7B',\n        messages: [\n          { role: 'user', content: 'What is the weather in San Francisco?' },\n          message,\n          {\n            role: 'tool',\n            tool_call_id: toolCall.id,\n            name: toolCall.function.name,\n            content: JSON.stringify(weatherResult)\n          }\n        ],\n        tools: tools\n      });\n    }\n  }\n}`,
        python: `tools = [\n    {\n        "type": "function",\n        "function": {\n            "name": "get_weather",\n            "description": "Get the current weather in a given location",\n            "parameters": {\n                "type": "object",\n                "properties": {\n                    "location": {\n                        "type": "string",\n                        "description": "The city and state, e.g. San Francisco, CA"\n                    },\n                    "unit": {\n                        "type": "string",\n                        "enum": ["celsius", "fahrenheit"]\n                    }\n                },\n                "required": ["location"]\n            }\n        }\n    }\n]\n\ncompletion = client.chat.completions.create(\n    model='deepseek-ai/DeepSeek-R1-Distill-Qwen-7B',\n    messages=[\n        {'role': 'user', 'content': 'What is the weather in San Francisco?'}\n    ],\n    tools=tools,\n    tool_choice='auto'\n)\n\nmessage = completion.choices[0].message\nif message.tool_calls:\n    for tool_call in message.tool_calls:\n        if tool_call.function.name == 'get_weather':\n            args = json.loads(tool_call.function.arguments)\n            weather_result = get_weather(args['location'], args.get('unit'))\n            \n            follow_up = client.chat.completions.create(\n                model='deepseek-ai/DeepSeek-R1-Distill-Qwen-7B',\n                messages=[\n                    {'role': 'user', 'content': 'What is the weather in San Francisco?'},\n                    message,\n                    {\n                        'role': 'tool',\n                        'tool_call_id': tool_call.id,\n                        'name': tool_call.function.name,\n                        'content': json.dumps(weather_result)\n                    }\n                ],\n                tools=tools\n            )`,
        curl: `curl https://YOUR_AGENT_ENDPOINT/v1/chat/completions \\\n  -H "Content-Type: application/json" \\\n  -H "x-api-key: YOUR_API_KEY" \\\n  -d '{\n    "model": "deepseek-ai/DeepSeek-R1-Distill-Qwen-7B",\n    "messages": [\n      {"role": "user", "content": "What is the weather in San Francisco?"}\n    ],\n    "tools": [\n      {\n        "type": "function",\n        "function": {\n          "name": "get_weather",\n          "description": "Get the current weather in a given location",\n          "parameters": {\n            "type": "object",\n            "properties": {\n              "location": {\n                "type": "string",\n                "description": "The city and state, e.g. San Francisco, CA"\n              },\n              "unit": {\n                "type": "string",\n                "enum": ["celsius", "fahrenheit"]\n              }\n            },\n            "required": ["location"]\n          }\n        }\n      }\n    ],\n    "tool_choice": "auto"\n  }'`
      }
    },
    {
      title: 'Authentication',
      content: 'Important: PioSphere uses x-api-key header instead of Authorization header. The OpenAI SDK handles this automatically when you set the apiKey parameter, but make sure your endpoint supports x-api-key authentication.',
      codeExamples: {
        javascript: 'The SDK automatically converts apiKey to x-api-key header',
        python: 'The SDK automatically converts api_key to x-api-key header',
        curl: 'Manually set: -H "x-api-key: YOUR_API_KEY"'
      }
    },
    {
      title: 'Key Differences',
      content: 'While PioSphere is OpenAI SDK compatible, note these differences:',
      differences: [
        {
          topic: 'Authentication',
          description: 'Uses x-api-key header instead of Authorization: Bearer token'
        },
        {
          topic: 'Base URL',
          description: 'Point to your PioSphere agent endpoint (e.g., https://chatbot.piosphere.ai/v1)'
        },
        {
          topic: 'Model Names',
          description: 'Use PioSphere model identifiers (e.g., deepseek-ai/DeepSeek-R1-Distill-Qwen-7B) instead of OpenAI model names'
        },
        {
          topic: 'Additional Features',
          description: 'PioSphere supports additional features like RAG, document management, and session management through dedicated endpoints'
        }
      ]
    }
  ]
};

export const systemOverview = {
  title: 'How PioSphere Works',
  sections: [
    {
      title: '1. Agent Chat APIs',
      description: 'Use the Chat APIs to interact with your AI agents. These APIs handle conversations, RAG-powered generation, document management, and session management.',
      baseUrl: 'https://YOUR_AGENT_ENDPOINT/api'
    },
    {
      title: '2. OpenAI SDK Compatibility',
      description: 'PioSphere endpoints are compatible with the OpenAI SDK, allowing you to use the official OpenAI client libraries with minimal configuration changes.'
    },
    {
      title: '3. Authentication',
      description: 'Agent Chat APIs use API key authentication via the x-api-key header. API keys can be generated and managed through the Agent Studio interface.'
    },
    {
      title: '4. RAG & Knowledge Base',
      description: 'Agents support Retrieval-Augmented Generation (RAG) by default. Upload documents, crawl websites, or ingest files to build a knowledge base. The agent will automatically use this knowledge when answering questions or generating responses.'
    }
  ]
};
